import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  KADRY_REQUEST_STATUS_LABELS,
  KADRY_REQUEST_STATUS_TONES,
  kadryPlural,
  type KadryOverviewRequestRow,
} from '@entities/kadry';
import { Badge } from '@shared/ui/Badge';
import { Table } from '@shared/ui/Table';
import { LivePulse, Panel, PanelHead, ago, toneColor } from './ui';

/* «Заявки в работе»: сначала те, где кандидаты ждут звонка или жалуются. */

const Scroll = styled.div`
  overflow-x: auto;
  margin: 0 ${({ theme }) => `-${theme.spacing(5)}`};
  -webkit-overflow-scrolling: touch;

  table {
    min-width: 640px;
  }

  th,
  td {
    white-space: nowrap;
    padding-left: ${({ theme }) => theme.spacing(3)};
    padding-right: ${({ theme }) => theme.spacing(3)};
  }

  th:first-child,
  td:first-child {
    padding-left: ${({ theme }) => theme.spacing(5)};
  }

  th:last-child,
  td:last-child {
    padding-right: ${({ theme }) => theme.spacing(5)};
  }

  th:first-child,
  td:first-child {
    white-space: normal;
    min-width: 220px;
  }

  tbody tr {
    cursor: pointer;
  }
`;

const Num = styled.td<{ $tone?: 'warning' | 'danger' | 'success' }>`
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-weight: ${({ $tone }) => ($tone ? 700 : 400)};
  && {
    color: ${({ theme, $tone }) => ($tone ? toneColor(theme, $tone) : theme.colors.text.primary)};
  }
`;

const NumTh = styled.th`
  && {
    text-align: right;
  }
`;

const Title = styled.div`
  font-weight: 600;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `4px ${theme.spacing(2)}`};
  margin-top: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};

  & > span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
  }
`;

const Empty = styled.div`
  padding: ${({ theme }) => theme.spacing(4)} 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

interface Props {
  rows: KadryOverviewRequestRow[];
  /** Всего открытых заявок (в таблице — первые по срочности). */
  total: number;
  delay?: number;
}

export function RequestsPanel({ rows, total, delay = 0 }: Props) {
  const navigate = useNavigate();
  return (
    <Panel $delay={delay}>
      <PanelHead>
        <h3>Заявки в работе</h3>
        <span>
          {total > rows.length ? `первые ${rows.length} из ${total} · ` : ''}сначала — где кандидаты
          ждут звонка
        </span>
      </PanelHead>
      {rows.length === 0 ? (
        <Empty>Открытых заявок нет.</Empty>
      ) : (
        <Scroll>
          <Table>
            <thead>
              <tr>
                <th>Заявка</th>
                <NumTh>Найдено</NumTh>
                <NumTh>Предложено</NumTh>
                <NumTh>Подходит</NumTh>
                <NumTh>Ждут звонка</NumTh>
                <NumTh>Не связались</NumTh>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  tabIndex={0}
                  onClick={() => navigate(`/agents/kadry/requests/${r.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') navigate(`/agents/kadry/requests/${r.id}`);
                  }}
                >
                  <td>
                    <Title>{r.title}</Title>
                    <Meta>
                      <Badge $tone={KADRY_REQUEST_STATUS_TONES[r.status]}>
                        {KADRY_REQUEST_STATUS_LABELS[r.status]}
                      </Badge>
                      <span>
                        ищем {r.headcount} {kadryPlural(r.headcount, ['человека', 'человека', 'человек'])}
                      </span>
                      {r.searching ? (
                        <span>
                          · <LivePulse /> идёт поиск
                        </span>
                      ) : (
                        r.last_search_at && <span>· поиск {ago(r.last_search_at)}</span>
                      )}
                    </Meta>
                  </td>
                  <Num>{r.found}</Num>
                  <Num>{r.offered}</Num>
                  <Num $tone={r.interested > 0 ? 'success' : undefined}>{r.interested}</Num>
                  <Num $tone={r.waiting_call > 0 ? 'warning' : undefined}>{r.waiting_call}</Num>
                  <Num $tone={r.not_contacted > 0 ? 'danger' : undefined}>{r.not_contacted}</Num>
                </tr>
              ))}
            </tbody>
          </Table>
        </Scroll>
      )}
    </Panel>
  );
}
